import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useIsNativeSession } from '@/store/auth';

import { useTaskCreateForm } from './useTaskCreateForm';
import { TaskCreateModalDesktop } from './TaskCreateModalDesktop';
import { TaskV2CreateModal } from './TaskV2CreateModal';
import type { TaskV2CreateFormInitialData } from './v2/useTaskV2CreateForm';
import { ConfirmCloseModal } from './ConfirmCloseModal';
import type { TaskApiPayload } from '@/features/tasks/model/forms/useTaskForm';

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaskApiPayload) => void | Promise<void>;
  initialData?: Record<string, unknown>;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Предзаполнение legacy-формы → предзаполнение формы v2. Переносим только то,
 * что у задач v2 вообще есть: срок, план в часах, название и описание.
 *
 * Осознанно НЕ переносим: PARENT_ID (вызывающий код приводит его к числу, а в
 * v2 это UUID), GROUP_ID (числовая группа Bitrix) и признак встречи —
 * подзадачи и встречи переезжают отдельно.
 */
function toV2InitialData(
  initialData: Record<string, unknown> | undefined
): TaskV2CreateFormInitialData | undefined {
  if (!initialData) {
    return undefined;
  }
  const hours = Number(initialData.TIME_ESTIMATE_HOURS);

  return {
    DEADLINE: str(initialData.DEADLINE),
    TIME_ESTIMATE_HOURS:
      Number.isFinite(hours) && hours > 0 ? hours : undefined,
    TITLE: str(initialData.TITLE),
    DESCRIPTION: str(initialData.DESCRIPTION),
  };
}

/**
 * Создание задачи. Под нативной сессией открывает форму v2, под сессией
 * Bitrix24 — прежнюю.
 *
 * Развилка живёт здесь, а не у вызывающих: модалку открывают из десяти мест
 * (плюс в панели задач, клик по ячейке календаря, шапка, сайдбар, чат задачи,
 * подзадачи), и в нативной сессии старая форма нерабочая в принципе — она
 * собирает payload для `/tasks/add`, а эта группа в новом бэке удалена.
 *
 * Важно, что развилка стоит ДО `useTaskCreateForm`: тот тянет справочники
 * legacy (пользователи, группы, список задач, смарт-процессы), и под нативной
 * сессией эти запросы не должны уходить вовсе.
 */
export default function TaskCreateModal(props: TaskCreateModalProps) {
  const isNativeSession = useIsNativeSession();

  // Правка задачи на v2 ещё не сделана — там остаётся прежняя форма.
  if (isNativeSession && props.mode !== 'edit') {
    return (
      <TaskV2CreateModal
        open={props.open}
        onClose={props.onClose}
        initialData={toV2InitialData(props.initialData)}
      />
    );
  }

  return <TaskCreateModalLegacy {...props} />;
}

/**
 * Orchestrator: форма одна и адаптивная — мобильная раскладка описана в самом
 * прототипе (порог 620px) и живёт внутри `TaskCreateModalDesktop`, поэтому
 * отдельного мобильного экрана больше нет.
 *
 * The dialog is a card centered with auto margins inside a full-screen scroll
 * container — so it grows with its content and the whole modal scrolls as a page
 * (no inner max-height juggling, no layout jumps when content changes height).
 */
function TaskCreateModalLegacy({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  mode = 'create',
}: TaskCreateModalProps) {
  const form = useTaskCreateForm({
    open,
    onClose,
    onSubmit,
    initialData,
    mode,
  });

  /* ─── Закрытие только через крестик (+ подтверждение) ───
   * Клика вне формы недостаточно — легко нажать случайно и потерять всё
   * написанное. Esc работает как крестик: не закрывает форму напрямую, а
   * открывает подтверждение; повторный Esc отменяет уже открытое подтверждение. */
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmCloseOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setConfirmCloseOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
      {createPortal(
        <form onSubmit={form.submit}>
          <div className="fixed inset-0 z-[9998] flex justify-center overflow-y-auto bg-black/50 px-3 py-4 lg:p-6 scrollbar-primary animate-in fade-in-0 duration-300">
            <TaskCreateModalDesktop
              form={form}
              isLoading={isLoading}
              mode={mode}
              onRequestClose={() => setConfirmCloseOpen(true)}
            />
          </div>
        </form>,
        document.body
      )}

      <ConfirmCloseModal
        open={confirmCloseOpen}
        onCancel={() => setConfirmCloseOpen(false)}
        onConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
      />
    </>
  );
}
